import { useState, useEffect, useContext } from "react";
import { UserContext, API } from "@/App";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Users, Copy, Gift, TrendingUp, Link2, Check, Share2,
  Twitter, Send, MessageCircle
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

export const ReferralPage = () => {
  const { user, refreshBalance, token } = useContext(UserContext);
  const [referralInfo, setReferralInfo] = useState(null);
  const [inputCode, setInputCode] = useState("");
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReferralInfo();
  }, []);

  const fetchReferralInfo = async () => {
    try {
      const response = await axios.get(`${API}/referral/info`, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` }
      });
      setReferralInfo(response.data);
    } catch (error) {
      console.error("Fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  const applyCode = async () => {
    if (!inputCode.trim()) {
      toast.error("Please enter a referral code");
      return;
    }

    try {
      const response = await axios.post(`${API}/referral/apply?code=${inputCode}`, {}, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      await refreshBalance();
      setInputCode("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid referral code");
    }
  };

  const shareOnTwitter = () => {
    const text = encodeURIComponent(`Join me on BetIndia and get ₹50 bonus! Use my code: ${referralInfo?.referral_code}`);
    window.open(`https://twitter.com/intent/tweet?text=${text}`, '_blank');
  };

  const shareOnTelegram = () => {
    const text = encodeURIComponent(`Join me on BetIndia and get ₹50 bonus! Use my code: ${referralInfo?.referral_code}\n${referralInfo?.referral_link}`);
    window.open(`https://t.me/share/url?url=${text}`, '_blank');
  };

  const shareOnWhatsApp = () => {
    const text = encodeURIComponent(`Join me on BetIndia and get ₹50 bonus! Use my code: ${referralInfo?.referral_code}\n${referralInfo?.referral_link}`);
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="w-12 h-12 border-4 border-[#00E701] border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#00E701] to-[#007AFF] flex items-center justify-center">
            <Users className="w-6 h-6 text-black" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white font-['Rajdhani']">Refer & Earn</h1>
            <p className="text-[#A1A1AA] text-sm">Invite friends and earn rewards together</p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-5 rounded-xl bg-[#1A1B1F] border border-white/10">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-[#00E701]/20 flex items-center justify-center">
                <Users className="w-5 h-5 text-[#00E701]" />
              </div>
              <span className="text-[#A1A1AA]">Total Referrals</span>
            </div>
            <p className="text-3xl font-bold text-white font-mono">
              {referralInfo?.total_referrals || 0}
            </p>
          </div>
          
          <div className="p-5 rounded-xl bg-[#1A1B1F] border border-white/10">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-[#FFD600]/20 flex items-center justify-center">
                <Gift className="w-5 h-5 text-[#FFD600]" />
              </div>
              <span className="text-[#A1A1AA]">Total Earned</span>
            </div>
            <p className="text-3xl font-bold text-[#FFD600] font-mono">
              ₹{referralInfo?.total_earnings || 0}
            </p>
          </div>
          
          <div className="p-5 rounded-xl bg-[#1A1B1F] border border-white/10">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-[#FF6B00]/20 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-[#FF6B00]" />
              </div>
              <span className="text-[#A1A1AA]">Commission Rate</span>
            </div>
            <p className="text-3xl font-bold text-white font-mono">
              {(referralInfo?.commission_rate || 0) * 100}%
            </p>
          </div>
        </div>

        {/* Your Referral Code */}
        <div className="p-6 rounded-2xl bg-gradient-to-br from-[#00E701]/10 to-transparent border border-[#00E701]/30">
          <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <Link2 className="w-5 h-5 text-[#00E701]" />
            Your Referral Code
          </h3>
          
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 p-4 rounded-xl bg-black/30 border border-white/10">
                <span className="text-3xl font-bold text-[#00E701] font-mono tracking-wider flex-1">
                  {referralInfo?.referral_code}
                </span>
                <Button
                  onClick={() => copyToClipboard(referralInfo?.referral_code)}
                  variant="outline"
                  size="icon"
                  className="border-[#00E701]/50 text-[#00E701]"
                >
                  {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                </Button>
              </div>
            </div>
            
            <div className="flex flex-col gap-2">
              <Button
                onClick={() => copyToClipboard(referralInfo?.referral_link)}
                className="bg-[#00E701] hover:bg-[#00C201] text-black font-bold"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy Link
              </Button>
            </div>
          </div>
          
          {/* Share buttons */}
          <div className="mt-4 flex items-center gap-3">
            <span className="text-[#A1A1AA] text-sm">Share:</span>
            <button 
              onClick={shareOnTwitter}
              className="w-10 h-10 rounded-lg bg-[#1DA1F2]/20 flex items-center justify-center hover:bg-[#1DA1F2]/30 transition-colors"
            >
              <Twitter className="w-5 h-5 text-[#1DA1F2]" />
            </button>
            <button 
              onClick={shareOnTelegram}
              className="w-10 h-10 rounded-lg bg-[#0088cc]/20 flex items-center justify-center hover:bg-[#0088cc]/30 transition-colors"
            >
              <Send className="w-5 h-5 text-[#0088cc]" />
            </button>
            <button 
              onClick={shareOnWhatsApp}
              className="w-10 h-10 rounded-lg bg-[#25D366]/20 flex items-center justify-center hover:bg-[#25D366]/30 transition-colors"
            >
              <MessageCircle className="w-5 h-5 text-[#25D366]" />
            </button>
          </div>
        </div>

        {/* Apply Referral Code */}
        <div className="p-6 rounded-2xl bg-[#1A1B1F] border border-white/10">
          <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <Gift className="w-5 h-5 text-[#FFD600]" />
            Have a Referral Code?
          </h3>
          
          <div className="flex gap-3">
            <Input
              value={inputCode}
              onChange={(e) => setInputCode(e.target.value.toUpperCase())}
              placeholder="Enter referral code (e.g., BETABC123)"
              className="flex-1 h-12 bg-black/20 border-white/10 text-white uppercase tracking-wider"
              maxLength={12}
            />
            <Button
              onClick={applyCode}
              className="bg-[#FFD600] hover:bg-[#E6C200] text-black font-bold px-6"
            >
              Apply
            </Button>
          </div>
          <p className="text-[#52525B] text-sm mt-2">
            Get ₹50 bonus when you use a friend's referral code!
          </p>
        </div>

        {/* How it works */}
        <div className="p-6 rounded-2xl bg-[#1A1B1F] border border-white/10">
          <h3 className="text-lg font-bold text-white mb-4">How It Works</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex flex-col items-center text-center p-4">
              <div className="w-14 h-14 rounded-full bg-[#00E701]/20 flex items-center justify-center mb-3">
                <Share2 className="w-6 h-6 text-[#00E701]" />
              </div>
              <h4 className="text-white font-bold mb-1">1. Share Your Code</h4>
              <p className="text-[#A1A1AA] text-sm">
                Share your unique referral code with friends
              </p>
            </div>
            
            <div className="flex flex-col items-center text-center p-4">
              <div className="w-14 h-14 rounded-full bg-[#FFD600]/20 flex items-center justify-center mb-3">
                <Users className="w-6 h-6 text-[#FFD600]" />
              </div>
              <h4 className="text-white font-bold mb-1">2. Friends Sign Up</h4>
              <p className="text-[#A1A1AA] text-sm">
                They register and use your code - both get bonuses!
              </p>
            </div>
            
            <div className="flex flex-col items-center text-center p-4">
              <div className="w-14 h-14 rounded-full bg-[#FF6B00]/20 flex items-center justify-center mb-3">
                <TrendingUp className="w-6 h-6 text-[#FF6B00]" />
              </div>
              <h4 className="text-white font-bold mb-1">3. Earn Forever</h4>
              <p className="text-[#A1A1AA] text-sm">
                Earn {(referralInfo?.commission_rate || 0) * 100}% of their wagers forever!
              </p>
            </div>
          </div>
          
          <div className="mt-4 p-4 rounded-xl bg-[#FFD600]/10 border border-[#FFD600]/20">
            <p className="text-center text-[#FFD600]">
              <strong>You get ₹100</strong> when they sign up • 
              <strong> They get ₹50</strong> bonus • 
              <strong> +{(referralInfo?.commission_rate || 0) * 100}%</strong> of their lifetime wagers
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
};
